/**
 * 🔴🔴 ĐẦU TƯ ĐỊNH GIÁ ĐỘNG (NAV) — US-E20.F4-0210 · S03 · @UC9334–@UC9338. @gate money 🔴🔴.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * BÀI TOÁN GỐC: sổ cái goppy là **ĐƠN-TIỀN-TỆ, MỌI BÚT TOÁN CÂN BẰNG** (`assertBalanced` +
 * currency-mirror 3 tầng funds→ledger_accounts→ledger_lines). Tài sản biến động giá (crypto, cổ
 * phiếu, vàng) KHÔNG được phép phá bất biến đó.
 *
 * 🔴 ADR 0006 (PO CHỐT — xem docs/ADR/0006-asset-holding-nav.md), 3 luật KHÔNG THƯƠNG LƯỢNG:
 *
 *  1. **ASSET_HOLDING = ĐƠN VỊ TÀI SẢN, TÁCH KHỎI CASH.** Nắm giữ ghi bằng SỐ LƯỢNG ĐƠN VỊ
 *     (`unitsScaled` — BigInt ×10^8) + GIÁ VỐN (`costBasisMinor` — BigInt minor). Tuyệt đối KHÔNG
 *     trộn units vào tài khoản CASH.
 *
 *  2. **LÃI/LỖ CHƯA THỰC HIỆN (unrealized) = CHỈ LÀ VIEW.** Giá thị trường lên/xuống ⇒ **0 BÚT
 *     TOÁN**. Định giá lại danh mục là PHÉP TÍNH HIỂN THỊ đọc từ `nav_snapshots` — KHÔNG post ledger,
 *     KHÔNG chạm CASH. Đây là BẤT BIẾN SỐ MỘT của module: `markToMarket` ở đây trả về SỐ, không trả
 *     về bút toán, và KHÔNG có hàm nào trong file này sinh draft ledger từ chênh lệch giá.
 *
 *  3. **CHỈ BÁN THẬT MỚI GHI CASH.** Bán ⇒ CASH tăng (tiền về) / ASSET_HOLDING giảm (giá vốn ra) +
 *     lãi/lỗ ĐÃ THỰC HIỆN (realized) vào INCOME/EXPENSE. Bút toán vẫn CÂN, sổ vẫn ĐƠN-TIỀN-TỆ.
 *
 * ═══════════════════════════════════════════════════════════════════════════════════════════════
 * NO-FLOAT (B1) — HAI THANG SỐ KHÁC NHAU, ĐỪNG LẪN:
 *   · TIỀN  : BigInt **minor** (VND: 1 = 1 đồng). Không thang phụ.
 *   · ĐƠN VỊ: BigInt **scaled ×10^UNIT_SCALE_EXP (=10^8)** — crypto có tới 8–18 chữ số thập phân,
 *     dùng float là mất tiền. `unitsScaled = 100_000_000n` ⇔ **1 đơn vị nguyên** (1 BTC / 1 cổ phiếu).
 *     (Cùng phong cách `FX_RATE_SCALE` ×10^8 của E18.F3 — 1 tiền lệ, không đẻ quy ước mới.)
 *   · NAV   : BigInt minor **cho MỘT ĐƠN VỊ NGUYÊN** (`navPerUnitMinor`). VD 1 BTC = 2.500.000.000đ.
 *
 * LÀM TRÒN — XÁC ĐỊNH, CÓ LỢI CHO QUỸ (không bao giờ phát hành/chi thừa):
 *   · `unitsForContribution` : **FLOOR** ⇒ tiền góp KHÔNG BAO GIỜ mua được nhiều unit hơn giá trị
 *     thực ⇒ không pha loãng người vào trước bằng đồng lẻ.
 *   · `valueOfUnits`         : **FLOOR** ⇒ tiền trả ra khi bán KHÔNG BAO GIỜ vượt giá trị thực.
 *   · P/L = hiệu hai giá trị đã floor ⇒ signed, xác định, cộng dồn không lệch.
 *
 * THUẦN: 0 DB, 0 I/O, deterministic. Service chỉ GỌI (B3 — không nhân bản công thức).
 */

/* ---------------------------------------------------------------------------
 * Thang đơn vị tài sản (no-float)
 * ------------------------------------------------------------------------- */

/** Số chữ số thập phân của SỐ LƯỢNG đơn vị tài sản. units thật = unitsScaled / 10^UNIT_SCALE_EXP. */
export const UNIT_SCALE_EXP = 8;

/** Thang nhân đơn vị: 10^8. `unitsScaled` (BigInt) = số đơn vị × UNIT_SCALE. KHÔNG float. */
export const UNIT_SCALE: bigint = 10n ** BigInt(UNIT_SCALE_EXP);

const ZERO = 0n;

/* ---------------------------------------------------------------------------
 * Lỗi có tên (fail-loud — KHÔNG nuốt, KHÔNG "sửa" số cho tròn)
 * ------------------------------------------------------------------------- */

export const INVEST_NAV_INVALID_CODE = "INVEST_NAV_INVALID" as const;
export const INVEST_AMOUNT_INVALID_CODE = "INVEST_AMOUNT_INVALID" as const;
export const INVEST_UNITS_INVALID_CODE = "INVEST_UNITS_INVALID" as const;
export const INVEST_UNITS_INSUFFICIENT_CODE = "INVEST_UNITS_INSUFFICIENT" as const;
export const INVEST_UNITS_CONSERVATION_CODE = "INVEST_UNITS_CONSERVATION" as const;

/** NAV ≤ 0 ⇒ vô nghĩa (tài sản không thể có giá 0/âm trong sổ này). */
export class NavInvalidError extends Error {
  readonly code = INVEST_NAV_INVALID_CODE;
  constructor(navPerUnitMinor: bigint) {
    super(`[invest] NAV/đơn vị phải > 0 (nhận ${navPerUnitMinor}).`);
    this.name = "NavInvalidError";
  }
}

/** Số tiền góp ≤ 0, hoặc góp quá nhỏ ⇒ mua được 0 unit (tiền sẽ "bốc hơi" — chặn thay vì nuốt). */
export class InvestAmountError extends Error {
  readonly code = INVEST_AMOUNT_INVALID_CODE;
  constructor(message: string) {
    super(`[invest] ${message}`);
    this.name = "InvestAmountError";
  }
}

/** Số đơn vị ≤ 0 (mua/bán 0 đơn vị = không có giao dịch). */
export class InvestUnitsError extends Error {
  readonly code = INVEST_UNITS_INVALID_CODE;
  constructor(unitsScaled: bigint) {
    super(`[invest] số đơn vị phải > 0 (nhận ${unitsScaled}).`);
    this.name = "InvestUnitsError";
  }
}

/** Bán nhiều hơn số đang nắm giữ ⇒ 422 (không bán khống). */
export class InvestUnitsInsufficientError extends Error {
  readonly code = INVEST_UNITS_INSUFFICIENT_CODE;
  constructor(
    readonly requestedScaled: bigint,
    readonly heldScaled: bigint,
  ) {
    super(`[invest] bán ${requestedScaled} đơn vị nhưng chỉ nắm giữ ${heldScaled} (không bán khống).`);
    this.name = "InvestUnitsInsufficientError";
  }
}

/**
 * 🔴 Σ đơn vị của mọi thành viên ≠ tổng phát hành của vị thế ⇒ SỔ ĐƠN VỊ HỎNG. Fail-loud (không tự
 * "chỉnh" cho khớp — sai ở đây nghĩa là có người đang giữ đơn vị không ai phát hành, hoặc ngược lại).
 */
export class UnitsConservationError extends Error {
  readonly code = INVEST_UNITS_CONSERVATION_CODE;
  constructor(
    readonly totalIssuedScaled: bigint,
    readonly sumMemberUnitsScaled: bigint,
  ) {
    super(
      `[invest] Σ đơn vị thành viên (${sumMemberUnitsScaled}) ≠ tổng phát hành (${totalIssuedScaled}).`,
    );
    this.name = "UnitsConservationError";
  }
}

/* ---------------------------------------------------------------------------
 * Công thức thuần
 * ------------------------------------------------------------------------- */

/**
 * @UC9336 — SỐ ĐƠN VỊ mua được khi góp `contributedMinor` tại NAV hiện hành.
 *
 *   unitsScaled = FLOOR( contributedMinor × UNIT_SCALE / navPerUnitMinor )
 *
 * 🔴 CHỐNG PHA LOÃNG: cùng số tiền, NAV THẤP ⇒ mua được NHIỀU đơn vị hơn; NAV CAO ⇒ ít hơn. Người
 * vào sau khi giá đã tăng KHÔNG "ăn ké" phần lãi của người vào trước, vì họ mua ít đơn vị hơn.
 *
 * FLOOR (không half-up): đồng lẻ ở lại QUỸ dưới dạng tiền mặt chưa đủ mua 1 đơn vị-nhỏ-nhất, KHÔNG
 * phát hành đơn vị "khống" ⇒ Σ giá trị đơn vị ≤ Σ tiền đã bỏ ra (không bao giờ đẻ giá trị).
 *
 * @throws NavInvalidError · InvestAmountError (góp ≤ 0, hoặc quá nhỏ ⇒ 0 đơn vị)
 */
export function unitsForContribution(contributedMinor: bigint, navPerUnitMinor: bigint): bigint {
  if (navPerUnitMinor <= ZERO) throw new NavInvalidError(navPerUnitMinor);
  if (contributedMinor <= ZERO) {
    throw new InvestAmountError(`số tiền góp phải > 0 (nhận ${contributedMinor}).`);
  }
  const unitsScaled = (contributedMinor * UNIT_SCALE) / navPerUnitMinor; // BigInt ⇒ FLOOR (số dương)
  if (unitsScaled <= ZERO) {
    throw new InvestAmountError(
      `góp ${contributedMinor} tại NAV ${navPerUnitMinor} mua được 0 đơn vị — số tiền quá nhỏ; ` +
        `không ghi nhận khoản góp 0 đơn vị (tiền sẽ bốc hơi).`,
    );
  }
  return unitsScaled;
}

/**
 * @UC9336 — GIÁ TRỊ THỊ TRƯỜNG (minor) của `unitsScaled` đơn vị tại NAV cho trước.
 *
 *   valueMinor = FLOOR( unitsScaled × navPerUnitMinor / UNIT_SCALE )
 *
 * 🔴 ĐÂY CHỈ LÀ PHÉP TÍNH HIỂN THỊ/ĐỊNH GIÁ — hàm này KHÔNG sinh bút toán. Nó chỉ trở thành TIỀN
 * THẬT khi người dùng BÁN (S05 post CASH bằng đúng số này).
 */
export function valueOfUnits(unitsScaled: bigint, navPerUnitMinor: bigint): bigint {
  if (navPerUnitMinor <= ZERO) throw new NavInvalidError(navPerUnitMinor);
  if (unitsScaled < ZERO) throw new InvestUnitsError(unitsScaled);
  return (unitsScaled * navPerUnitMinor) / UNIT_SCALE; // FLOOR (số không âm)
}

/**
 * 🔴🔴 @UC9336 @UC9338 — LÃI/LỖ **CHƯA THỰC HIỆN** (signed, có thể ÂM).
 *
 *   unrealizedPnlMinor = valueOfUnits(units, navNow) − valueOfUnits(units, navEntry)
 *
 * 🔴 **CHỈ LÀ VIEW — TUYỆT ĐỐI KHÔNG SINH BÚT TOÁN.** Giá gấp đôi ⇒ hàm này trả số dương, và ĐÓ LÀ
 * TẤT CẢ: `count(ledger_transactions)`, `count(ledger_lines)`, Σ CASH **BẤT BIẾN**. Lãi trên giấy
 * KHÔNG PHẢI TIỀN MẶT — ghi nó vào CASH là NÓI DỐI người dùng (và mất khả năng đối chiếu sổ với
 * tiền thật trong két/ngân hàng).
 */
export function unrealizedPnlMinor(
  unitsScaled: bigint,
  navAtEntryMinor: bigint,
  navNowMinor: bigint,
): bigint {
  return valueOfUnits(unitsScaled, navNowMinor) - valueOfUnits(unitsScaled, navAtEntryMinor);
}

/**
 * 🔴 @UC9337 — LÃI/LỖ **ĐÃ THỰC HIỆN** khi BÁN THẬT (signed).
 *
 *   realizedPnlMinor = proceeds(unitsSold @ navSell) − costBasis(unitsSold @ navEntry)
 *
 * Đây là số DUY NHẤT được phép chạm INCOME/EXPENSE (S05): tiền đã về tài khoản, lãi/lỗ đã CHỐT.
 */
export function realizedPnlMinor(
  unitsSoldScaled: bigint,
  navAtEntryMinor: bigint,
  navSellMinor: bigint,
): bigint {
  if (unitsSoldScaled <= ZERO) throw new InvestUnitsError(unitsSoldScaled);
  return valueOfUnits(unitsSoldScaled, navSellMinor) - valueOfUnits(unitsSoldScaled, navAtEntryMinor);
}

/** Một LÔ đang nắm giữ của 1 thành viên (hàng `invest_units`): đơn vị còn lại + NAV lúc vào. */
export interface InvestLot {
  readonly id: string;
  readonly unitsScaled: bigint;
  readonly navAtEntryMinor: bigint;
}

/** Phần bán ra từ một lô (FIFO). */
export interface LotConsumption {
  readonly lotId: string;
  readonly unitsScaled: bigint;
  readonly navAtEntryMinor: bigint;
  /** Giá vốn của phần bán ra từ lô này (minor) — chính là số sẽ CREDIT ASSET_HOLDING. */
  readonly costBasisMinor: bigint;
}

/** Kết quả bổ lô khi bán. */
export interface LotAllocation {
  readonly consumptions: ReadonlyArray<LotConsumption>;
  /** Σ giá vốn phần bán ra (minor) — chân CREDIT ASSET_HOLDING. */
  readonly totalCostBasisMinor: bigint;
}

/**
 * @UC9337 — BỔ LÔ **FIFO** khi bán: đơn vị mua TRƯỚC bán TRƯỚC (deterministic, kiểm toán được;
 * không để service tự chọn lô ⇒ không "chọn lô có lợi" để đẹp báo cáo).
 *
 * `lots` PHẢI đã sắp theo thứ tự vào lệnh (cũ → mới). Bán vượt tổng nắm giữ ⇒
 * `InvestUnitsInsufficientError` (KHÔNG bán khống).
 */
export function allocateLotsFifo(
  lots: ReadonlyArray<InvestLot>,
  unitsToSellScaled: bigint,
): LotAllocation {
  if (unitsToSellScaled <= ZERO) throw new InvestUnitsError(unitsToSellScaled);

  const held = lots.reduce((sum, l) => sum + l.unitsScaled, ZERO);
  if (unitsToSellScaled > held) {
    throw new InvestUnitsInsufficientError(unitsToSellScaled, held);
  }

  const consumptions: LotConsumption[] = [];
  let remaining = unitsToSellScaled;
  let totalCostBasisMinor = ZERO;

  for (const lot of lots) {
    if (remaining === ZERO) break;
    if (lot.unitsScaled <= ZERO) continue;
    const take = lot.unitsScaled < remaining ? lot.unitsScaled : remaining;
    const costBasisMinor = valueOfUnits(take, lot.navAtEntryMinor);
    consumptions.push({
      lotId: lot.id,
      unitsScaled: take,
      navAtEntryMinor: lot.navAtEntryMinor,
      costBasisMinor,
    });
    totalCostBasisMinor += costBasisMinor;
    remaining -= take;
  }

  return { consumptions, totalCostBasisMinor };
}

/**
 * 🔴🔴 @UC9338 — BẤT BIẾN BẢO TOÀN ĐƠN VỊ: Σ đơn vị mọi thành viên = tổng phát hành của vị thế.
 * Lệch ⇒ ném `UnitsConservationError` (fail-loud). Service gọi SAU MỖI lần mua/bán (cùng tx) ⇒ sổ
 * đơn vị không bao giờ trôi.
 */
export function assertUnitsConserved(
  totalIssuedScaled: bigint,
  memberUnitsScaled: ReadonlyArray<bigint>,
): true {
  const sum = memberUnitsScaled.reduce((acc, u) => acc + u, ZERO);
  if (sum !== totalIssuedScaled) {
    throw new UnitsConservationError(totalIssuedScaled, sum);
  }
  return true;
}

/** Vị thế của 1 thành viên khi ĐỊNH GIÁ LẠI (view). */
export interface MemberHoldingInput {
  readonly groupMemberId: string;
  /** Các lô còn nắm giữ của thành viên đó. */
  readonly lots: ReadonlyArray<InvestLot>;
}

/** Dòng danh mục sau định giá lại (VIEW — 0 bút toán). */
export interface MemberValuation {
  readonly groupMemberId: string;
  readonly unitsScaled: bigint;
  readonly costBasisMinor: bigint;
  readonly currentValueMinor: bigint;
  /** signed — âm khi giá giảm. */
  readonly unrealizedPnlMinor: bigint;
}

/**
 * 🔴🔴 @UC9336 @UC9338 — ĐỊNH GIÁ LẠI DANH MỤC (mark-to-market).
 *
 * 🔴 **TRẢ VỀ SỐ, KHÔNG TRẢ VỀ BÚT TOÁN.** Đây là toàn bộ tác dụng của việc giá lên/xuống: một bảng
 * hiển thị. Không có nhánh nào ở đây (hay ở bất kỳ đâu trong `invest.ts`) sinh `LedgerTransactionDraft`
 * từ chênh lệch giá. Int-spec khẳng định: NAV × 2 ⇒ count(tx)/count(lines)/Σ CASH BẤT BIẾN.
 */
export function markToMarket(
  holdings: ReadonlyArray<MemberHoldingInput>,
  navNowMinor: bigint,
): ReadonlyArray<MemberValuation> {
  if (navNowMinor <= ZERO) throw new NavInvalidError(navNowMinor);
  return holdings.map((h) => {
    let unitsScaled = ZERO;
    let costBasisMinor = ZERO;
    let currentValueMinor = ZERO;
    for (const lot of h.lots) {
      unitsScaled += lot.unitsScaled;
      costBasisMinor += valueOfUnits(lot.unitsScaled, lot.navAtEntryMinor);
      currentValueMinor += valueOfUnits(lot.unitsScaled, navNowMinor);
    }
    return {
      groupMemberId: h.groupMemberId,
      unitsScaled,
      costBasisMinor,
      currentValueMinor,
      // signed: âm khi giá giảm (hiển thị SỰ THẬT, không che lỗ).
      unrealizedPnlMinor: currentValueMinor - costBasisMinor,
    };
  });
}

/** Chuỗi hiển thị số đơn vị (vd 150000000n → "1.50000000") — no-float, thao tác CHUỖI. */
export function formatUnitsScaled(unitsScaled: bigint): string {
  const negative = unitsScaled < ZERO;
  const abs = negative ? -unitsScaled : unitsScaled;
  const whole = abs / UNIT_SCALE;
  const frac = (abs % UNIT_SCALE).toString().padStart(UNIT_SCALE_EXP, "0");
  return `${negative ? "-" : ""}${whole}.${frac}`;
}
